/**
 * Step three — `clients_invite_member!`. The seats, and the first email anybody gets.
 *
 * `clients_create`'s own description: "the first owner is created if that address has
 * no account yet, and **is not emailed** until somebody invites or signs them in." So
 * this step is not decoration — it is where the account stops being a row in a
 * database and becomes something a client can open. An invitation is good for seven
 * days (`.docs/00-index.md` §6) and holds a seat while it is pending, which is why
 * `seats_used` counts it.
 *
 * It is skippable all the same: a studio setting an account up ahead of a signature
 * has a reason not to send anything yet, and the wizard is a sequence rather than a
 * transaction — coming back is `?step=owner&client=<slug>`.
 */

import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Stack } from '../../../components/portal/Layout.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { Muted, Prose, Small } from '../../../components/portal/Text.js'
import { ActionForm } from '../../../components/studio/ActionForm.js'
import { useFormat } from '../../../format/useFormat.js'
import { useAccount } from './account.js'

export type OwnerStepProps = {
  readonly client: string
  readonly onNext: () => void
}

export function OwnerStep({ client, onNext }: OwnerStepProps) {
  const format = useFormat()
  const { account } = useAccount(client)

  if (account === null) return <Empty>Reading who is on the account…</Empty>

  return (
    <Stack gap="loose">
      <Table>
        <THead>
          <Tr>
            <Th>Seat</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th right>State</Th>
          </Tr>
        </THead>
        <TBody>
          {account.members.map((member) => (
            <Tr key={member.member_id}>
              <Td>{member.name}</Td>
              <Td>
                <Small>{member.email}</Small>
              </Td>
              <Td>
                <Small>
                  <Muted>{member.role}</Muted>
                </Small>
              </Td>
              <Td right>
                <Small>
                  <Muted>on the account</Muted>
                </Small>
              </Td>
            </Tr>
          ))}
          {account.invitations.map((invitation) => (
            <Tr key={invitation.invitation_id} variant="sub">
              <Td>
                <Muted>—</Muted>
              </Td>
              <Td>
                <Small>{invitation.email}</Small>
              </Td>
              <Td>
                <Small>
                  <Muted>{invitation.role}</Muted>
                </Small>
              </Td>
              <Td right>
                <Small>
                  <Muted>invited, expires {format.date(invitation.expires_at)}</Muted>
                </Small>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      <Small>
        <Muted>
          {format.number(account.seats_used)} of {format.number(account.client.seat_limit)} seats
          taken. A pending invitation holds one.
        </Muted>
      </Small>

      <ActionForm
        action="clients_invite_member"
        fixed={{ client }}
        submitLabel="Send the invitation"
      >
        <Prose>
          An invitation is an email with a link and a role on it, good for seven days. Invite the
          owner named above to give them their way in, and anybody else who needs one —{' '}
          <b>billing</b> sees and pays invoices, <b>admin</b> approves money and manages seats,{' '}
          <b>member</b> comments and raises requests.
        </Prose>
      </ActionForm>

      <ButtonRow>
        <Button onClick={onNext} variant="primary">
          Carry on to the package
        </Button>
      </ButtonRow>
    </Stack>
  )
}
